import React from "react";
import { Box, Button, Link, Typography } from "@mui/material";
import { FONT_BODY, FONT_HEADING, RADIUS } from "../styles";
import { SUPPORT_EMAIL } from "../constants";

/**
 * Catches render/lifecycle errors below it so one broken section can't white-screen the console.
 *
 * The common trigger isn't a component bug — it's a stale chunk. The admin pages are code-split
 * (React.lazy, ENG-2571) and the app deploys to Firebase Hosting as atomic releases, so a tab left
 * open across a deploy requests a hashed chunk that no longer exists and the lazy import REJECTS.
 * `Suspense` only covers the pending state; a rejected import throws, and with no boundary that took
 * out the entire dashboard with a blank page and no way back.
 *
 * A chunk failure is recoverable by reloading (the reload fetches the new index.html and its chunk
 * names), so it's offered as the primary action and worded as an update rather than an error — which
 * is what it is.
 */

/** Chunk-load failures are the stale-deploy case, not a bug in the page. */
const isChunkLoadError = (error: Error): boolean =>
  error.name === "ChunkLoadError" ||
  /Loading chunk [\w-]+ failed|Loading CSS chunk|dynamically imported module/i.test(error.message);

interface Props {
  children: React.ReactNode;
  /**
   * The failed page's display name for the fallback copy, e.g. "Members & Seats" — never its PageId
   * (ENG-3468: the console passed `activePage`, so the copy read "the saas-inventory page"). Omit it
   * for the whole app, or for a page without a name, and the copy says "this page".
   */
  section?: string;
  /** Remount attempt for a section-level boundary — lets the admin retry without a full reload. */
  onReset?: () => void;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // No error-reporting service is wired up in this app yet; the console is what a support session
    // has to work with, so make sure the component stack is in it.
    console.error("Unhandled UI error:", error, info.componentStack);
  }

  private handleReset = () => {
    this.setState({ error: null });
    this.props.onReset?.();
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const stale = isChunkLoadError(error);
    const where = this.props.section ? `the ${this.props.section} page` : "this page";

    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5, py: 8, px: 3, textAlign: "center" }}>
        <Typography sx={{ fontFamily: FONT_HEADING, fontWeight: 700, fontSize: "18px" }}>
          {stale ? "Haven has been updated" : `Something went wrong loading ${where}`}
        </Typography>
        <Typography sx={{ fontFamily: FONT_BODY, fontSize: "14px", color: "text.secondary", maxWidth: 420 }}>
          {stale
            ? "This tab is running an older version. Reload to pick up the latest — nothing you've saved is affected."
            : (
              <>
                The rest of your account is still available. Reloading usually clears this; if it keeps
                happening,{" "}
                <Link href={`mailto:${SUPPORT_EMAIL}`} sx={{ color: "inherit", textUnderlineOffset: "2px" }}>
                  let us know
                </Link>{" "}
                what you were doing.
              </>
            )}
        </Typography>
        <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
          <Button
            variant="contained"
            onClick={() => window.location.reload()}
            sx={{ fontFamily: FONT_BODY, fontWeight: 600, textTransform: "none", borderRadius: RADIUS.pill }}
          >
            Reload
          </Button>
          {/* Retrying in place only makes sense for a transient render error — a stale chunk needs
              the new index.html, so it isn't offered there. */}
          {!stale && this.props.onReset && (
            <Button
              onClick={this.handleReset}
              sx={{ fontFamily: FONT_BODY, textTransform: "none", borderRadius: RADIUS.pill, color: "text.secondary" }}
            >
              Try again
            </Button>
          )}
        </Box>
      </Box>
    );
  }
}
